import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import {
  Estate_CreateEstateInput,
  EstateInputDataFormat_CreateEstateInputDataFormatInput,
  EstateInputDataFormatCategory,
  EstateInputDataFormatDataType,
  EstateInputDataFormatLinkedField,
  EstateInputDataFormatType,
  EstateInputDataFormatUpdateRule,
  useDocumentTag_GetAllDocumentTagsQuery,
} from 'queries';

import FormSelectField from 'common/form-select-field';
import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    textAlign: 'left',
  },
  pillContainer: {
    display: 'flex',
  },
  pill: {
    padding: '4px 8px',
    borderRadius: '10px',
  },
  label: {
    textTransform: 'capitalize',
  },
  infoText: {
    marginTop: '20px',
    fontSize: '14px',
    color: '#666',
  },
}));

export const linkedEstateMatterFieldOptions: Record<EstateInputDataFormatLinkedField, string> = {
  [EstateInputDataFormatLinkedField.ClientAddressCity]: 'Client Address City',
  [EstateInputDataFormatLinkedField.ClientAddressState]: 'Client Address State',
  [EstateInputDataFormatLinkedField.ClientAddressStreet]: 'Client Address Street',
  [EstateInputDataFormatLinkedField.ClientAddressZip]: 'Client Address Zip',
  [EstateInputDataFormatLinkedField.ClientDateOfBirth]: 'Client Date of Birth',
  [EstateInputDataFormatLinkedField.ClientEmailAddress]: 'Client Email Address',
  [EstateInputDataFormatLinkedField.ClientFirstName]: 'Client First Name',
  [EstateInputDataFormatLinkedField.ClientLastName]: 'Client Last Name',
  [EstateInputDataFormatLinkedField.ClientMiddleName]: 'Client Middle Name',
  [EstateInputDataFormatLinkedField.ClientPhoneNumber]: 'Client Phone Number',
  [EstateInputDataFormatLinkedField.ClientSsn]: 'Client SSN',
  [EstateInputDataFormatLinkedField.ClientRoleWithinEstate]: 'Client Role Within Estate',
  [EstateInputDataFormatLinkedField.CorporatePersonalRepresentativeName]:
    'Corporate Personal Representative Name',
  [EstateInputDataFormatLinkedField.DecedentFirstName]: 'Decedent First Name',
  [EstateInputDataFormatLinkedField.DecedentDateOfBirth]: 'Decedent Date of Birth',
  [EstateInputDataFormatLinkedField.DecedentDateOfDeath]: 'Decedent Date of Death',
  [EstateInputDataFormatLinkedField.DecedentLastAddressCity]: 'Decedent Last Address City',
  [EstateInputDataFormatLinkedField.DecedentLastAddressState]: 'Decedent Last Address State',
  [EstateInputDataFormatLinkedField.DecedentLastAddressStreet]: 'Decedent Last Address Street',
  [EstateInputDataFormatLinkedField.DecedentLastAddressZip]: 'Decedent Last Address Zip',
  [EstateInputDataFormatLinkedField.DecedentLastName]: 'Decedent Last Name',
  [EstateInputDataFormatLinkedField.DecedentMiddleName]: 'Decedent Middle Name',
  [EstateInputDataFormatLinkedField.DecedentSsn]: 'Decedent SSN',
  [EstateInputDataFormatLinkedField.Ein]: 'EIN',
  [EstateInputDataFormatLinkedField.SpouseAddressCity]: 'Spouse Address City',
  [EstateInputDataFormatLinkedField.SpouseAddressState]: 'Spouse Address State',
  [EstateInputDataFormatLinkedField.SpouseAddressStreet]: 'Spouse Address Street',
  [EstateInputDataFormatLinkedField.SpouseAddressZip]: 'Spouse Address Zip',
  [EstateInputDataFormatLinkedField.SpouseDateOfBirth]: 'Spouse Date of Birth',
  [EstateInputDataFormatLinkedField.SpouseEmailAddress]: 'Spouse Email Address',
  [EstateInputDataFormatLinkedField.SpouseFirstName]: 'Spouse First Name',
  [EstateInputDataFormatLinkedField.SpouseLastName]: 'Spouse Last Name',
  [EstateInputDataFormatLinkedField.SpouseMiddleName]: 'Spouse Middle Name',
  [EstateInputDataFormatLinkedField.SpousePhoneNumber]: 'Spouse Phone Number',
  [EstateInputDataFormatLinkedField.SpouseSsn]: 'Spouse SSN',
  [EstateInputDataFormatLinkedField.TrustDate]: 'Trust Date',
  [EstateInputDataFormatLinkedField.TrustEin]: 'Trust EIN',
  [EstateInputDataFormatLinkedField.TrustName]: 'Trust Name',
  [EstateInputDataFormatLinkedField.WardAddressCity]: 'Ward Address City',
  [EstateInputDataFormatLinkedField.WardAddressState]: 'Ward Address State',
  [EstateInputDataFormatLinkedField.WardAddressStreet]: 'Ward Address Street',
  [EstateInputDataFormatLinkedField.WardAddressZip]: 'Ward Address Zip',
  [EstateInputDataFormatLinkedField.WardDateOfBirth]: 'Ward Date of Birth',
  [EstateInputDataFormatLinkedField.WardFirstName]: 'Ward First Name',
  [EstateInputDataFormatLinkedField.WardLastName]: 'Ward Last Name',
  [EstateInputDataFormatLinkedField.WardMiddleName]: 'Ward Middle Name',
  [EstateInputDataFormatLinkedField.WardSsn]: 'Ward SSN',
  [EstateInputDataFormatLinkedField.ChildFirstName_1]: 'Child First Name 1',
  [EstateInputDataFormatLinkedField.ChildLastName_1]: 'Child Last Name 1',
  [EstateInputDataFormatLinkedField.ChildMiddleName_1]: 'Child Middle Name',
  [EstateInputDataFormatLinkedField.ChildFirstName_2]: 'Child First Name 2',
  [EstateInputDataFormatLinkedField.ChildLastName_2]: 'Child Last Name 2',
  [EstateInputDataFormatLinkedField.ChildMiddleName_2]: 'Child Middle Name 2',
  [EstateInputDataFormatLinkedField.ChildFirstName_3]: 'Child First Name 3',
  [EstateInputDataFormatLinkedField.ChildLastName_3]: 'Child Last Name 3',
  [EstateInputDataFormatLinkedField.ChildMiddleName_3]: 'Child Middle Name 3',
  [EstateInputDataFormatLinkedField.ChildFirstName_4]: 'Child First Name 4',
  [EstateInputDataFormatLinkedField.ChildLastName_4]: 'Child Last Name 4',
  [EstateInputDataFormatLinkedField.ChildMiddleName_4]: 'Child Middle Name 4',
  [EstateInputDataFormatLinkedField.ChildFirstName_5]: 'Child First Name 5',
  [EstateInputDataFormatLinkedField.ChildLastName_5]: 'Child Last Name 5',
  [EstateInputDataFormatLinkedField.ChildMiddleName_5]: 'Child Middle Name 5',
  [EstateInputDataFormatLinkedField.ChildFirstName_6]: 'Child First Name 6',
  [EstateInputDataFormatLinkedField.ChildLastName_6]: 'Child Last Name 6',
  [EstateInputDataFormatLinkedField.ChildMiddleName_6]: 'Child Middle Name 6',
  [EstateInputDataFormatLinkedField.ExecutorFirstName_1]: 'Executor First Name 1',
  [EstateInputDataFormatLinkedField.ExecutorLastName_1]: 'Executor Last Name 1',
  [EstateInputDataFormatLinkedField.ExecutorMiddleName_1]: 'Executor Middle Name 1',
  [EstateInputDataFormatLinkedField.ExecutorFirstName_2]: 'Executor First Name 2',
  [EstateInputDataFormatLinkedField.ExecutorLastName_2]: 'Executor Last Name 2',
  [EstateInputDataFormatLinkedField.ExecutorMiddleName_2]: 'Executor Middle Name 2',
  [EstateInputDataFormatLinkedField.GrantorFirstName_1]: 'Grantor First Name 1',
  [EstateInputDataFormatLinkedField.GrantorLastName_1]: 'Grantor Last Name 1',
  [EstateInputDataFormatLinkedField.GrantorMiddleName_1]: 'Grantor Middle Name 1',
  [EstateInputDataFormatLinkedField.GrantorSsn_1]: 'Grantor SSN 1',
  [EstateInputDataFormatLinkedField.GrantorAddressStreet_1]: 'Grantor Address Street 1',
  [EstateInputDataFormatLinkedField.GrantorAddressCity_1]: 'Grantor Address City 1',
  [EstateInputDataFormatLinkedField.GrantorAddressState_1]: 'Grantor Address State 1',
  [EstateInputDataFormatLinkedField.GrantorAddressZip_1]: 'Grantor Address Zip 1',

  [EstateInputDataFormatLinkedField.GrantorFirstName_2]: 'Grantor First Name 2',
  [EstateInputDataFormatLinkedField.GrantorLastName_2]: 'Grantor Last Name 2',
  [EstateInputDataFormatLinkedField.GrantorMiddleName_2]: 'Grantor Middle Name 2',
  [EstateInputDataFormatLinkedField.GrantorSsn_2]: 'Grantor SSN 2',
  [EstateInputDataFormatLinkedField.GrantorAddressStreet_2]: 'Grantor Address Street 2',
  [EstateInputDataFormatLinkedField.GrantorAddressCity_2]: 'Grantor Address City 2',
  [EstateInputDataFormatLinkedField.GrantorAddressState_2]: 'Grantor Address State 2',
  [EstateInputDataFormatLinkedField.GrantorAddressZip_2]: 'Grantor Address Zip 2',
  [EstateInputDataFormatLinkedField.GuardianFirstName_1]: 'Guardian First Name 1',
  [EstateInputDataFormatLinkedField.GuardianLastName_1]: 'Guardian Last Name 1',
  [EstateInputDataFormatLinkedField.GuardianMiddleName_1]: 'Guardian Middle Name 1',
  [EstateInputDataFormatLinkedField.GuardianFirstName_2]: 'Guardian First Name 2',
  [EstateInputDataFormatLinkedField.GuardianLastName_2]: 'Guardian Last Name 2',
  [EstateInputDataFormatLinkedField.GuardianMiddleName_2]: 'Guardian Middle Name 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentFirstName_1]: 'Healthcare Agent First Name 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentLastName_1]: 'Healthcare Agent Last Name 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentMiddleName_1]: 'Healthcare Agent Middle Name 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentPhoneNumber_1]:
    'Healthcare Agent Phone Number 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressStreet_1]:
    'Healthcare Agent Address Street 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressCity_1]:
    'Healthcare Agent Address City 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressState_1]:
    'Healthcare Agent Address State 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressZip_1]: 'Healthcare Agent Address Zip 1',
  [EstateInputDataFormatLinkedField.HealthcareAgentFirstName_2]: 'Healthcare Agent First Name 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentLastName_2]: 'Healthcare Agent Last Name 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentMiddleName_2]: 'Healthcare Agent Middle Name 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentPhoneNumber_2]:
    'Healthcare Agent Phone Number 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressStreet_2]:
    'Healthcare Agent Address Street 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressCity_2]:
    'Healthcare Agent Address City 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressState_2]:
    'Healthcare Agent Address State 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressZip_2]: 'Healthcare Agent Address Zip 2',
  [EstateInputDataFormatLinkedField.HealthcareAgentFirstName_3]: 'Healthcare Agent First Name 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentLastName_3]: 'Healthcare Agent Last Name 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentMiddleName_3]: 'Healthcare Agent Middle Name 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentPhoneNumber_3]:
    'Healthcare Agent Phone Number 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressStreet_3]:
    'Healthcare Agent Address Street 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressCity_3]:
    'Healthcare Agent Address City 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressState_3]:
    'Healthcare Agent Address State 3',
  [EstateInputDataFormatLinkedField.HealthcareAgentAddressZip_3]: 'Healthcare Agent Address Zip 3',
  [EstateInputDataFormatLinkedField.PoaFirstName_1]: 'POA First Name 1',
  [EstateInputDataFormatLinkedField.PoaLastName_1]: 'POA Last Name 1',
  [EstateInputDataFormatLinkedField.PoaMiddleName_1]: 'POA Middle Name 1',
  [EstateInputDataFormatLinkedField.PoaPhoneNumber_1]: 'POA Phone Number 1',
  [EstateInputDataFormatLinkedField.PoaEmail_1]: 'POA Email 1',
  [EstateInputDataFormatLinkedField.PoaAddressStreet_1]: 'POA Address Street 1',
  [EstateInputDataFormatLinkedField.PoaAddressCity_1]: 'POA Address City 1',
  [EstateInputDataFormatLinkedField.PoaAddressState_1]: 'POA Address State 1',
  [EstateInputDataFormatLinkedField.PoaAddressZip_1]: 'POA Address Zip 1',
  [EstateInputDataFormatLinkedField.PoaFirstName_2]: 'POA First Name 2',
  [EstateInputDataFormatLinkedField.PoaLastName_2]: 'POA Last Name 2',
  [EstateInputDataFormatLinkedField.PoaMiddleName_2]: 'POA Middle Name 2',
  [EstateInputDataFormatLinkedField.PoaPhoneNumber_2]: 'POA Phone Number 2',
  [EstateInputDataFormatLinkedField.PoaEmail_2]: 'POA Email 2',
  [EstateInputDataFormatLinkedField.PoaAddressStreet_2]: 'POA Address Street 2',
  [EstateInputDataFormatLinkedField.PoaAddressCity_2]: 'POA Address City 2',
  [EstateInputDataFormatLinkedField.PoaAddressState_2]: 'POA Address State 2',
  [EstateInputDataFormatLinkedField.PoaAddressZip_2]: 'POA Address Zip 2',
  [EstateInputDataFormatLinkedField.PoaFirstName_3]: 'POA First Name 3',
  [EstateInputDataFormatLinkedField.PoaLastName_3]: 'POA Last Name 3',
  [EstateInputDataFormatLinkedField.PoaMiddleName_3]: 'POA Middle Name 3',
  [EstateInputDataFormatLinkedField.PoaPhoneNumber_3]: 'POA Phone Number 3',
  [EstateInputDataFormatLinkedField.PoaEmail_3]: 'POA Email 3',
  [EstateInputDataFormatLinkedField.PoaAddressStreet_3]: 'POA Address Street 3',
  [EstateInputDataFormatLinkedField.PoaAddressCity_3]: 'POA Address City 3',
  [EstateInputDataFormatLinkedField.PoaAddressState_3]: 'POA Address State 3',
  [EstateInputDataFormatLinkedField.PoaAddressZip_3]: 'POA Address Zip 3',

  [EstateInputDataFormatLinkedField.TrusteeFirstName_1]: 'Trustee First Name 1',
  [EstateInputDataFormatLinkedField.TrusteeLastName_1]: 'Trustee Last Name 1',
  [EstateInputDataFormatLinkedField.TrusteeMiddleName_1]: 'Trustee Middle Name 1',
  [EstateInputDataFormatLinkedField.TrusteeEmail_1]: 'Trustee Email 1',
  [EstateInputDataFormatLinkedField.TrusteePhoneNumber_1]: 'Trustee Phone Number 1',
  [EstateInputDataFormatLinkedField.TrusteeAddressCity_1]: 'Trust Address City 1',
  [EstateInputDataFormatLinkedField.TrusteeAddressState_1]: 'Trust Address State 1',
  [EstateInputDataFormatLinkedField.TrusteeAddressStreet_1]: 'Trust Address Street 1',
  [EstateInputDataFormatLinkedField.TrusteeAddressZip_1]: 'Trust Address Zip 1',

  [EstateInputDataFormatLinkedField.TrusteeFirstName_2]: 'Trustee First Name 2',
  [EstateInputDataFormatLinkedField.TrusteeLastName_2]: 'Trustee Last Name 2',
  [EstateInputDataFormatLinkedField.TrusteeMiddleName_2]: 'Trustee Middle Name ',
  [EstateInputDataFormatLinkedField.TrusteeEmail_2]: 'Trustee Email 2',
  [EstateInputDataFormatLinkedField.TrusteePhoneNumber_2]: 'Trustee Phone Number 2',
  [EstateInputDataFormatLinkedField.TrusteeAddressCity_2]: 'Trust Address City 2',
  [EstateInputDataFormatLinkedField.TrusteeAddressState_2]: 'Trust Address State 2',
  [EstateInputDataFormatLinkedField.TrusteeAddressStreet_2]: 'Trust Address Street 2',
  [EstateInputDataFormatLinkedField.TrusteeAddressZip_2]: 'Trust Address Zip 2',

  [EstateInputDataFormatLinkedField.TrusteeFirstName_3]: 'Trustee First Name 3',
  [EstateInputDataFormatLinkedField.TrusteeLastName_3]: 'Trustee Last Name 3',
  [EstateInputDataFormatLinkedField.TrusteeMiddleName_3]: 'Trustee Middle Name 3',
  [EstateInputDataFormatLinkedField.TrusteeEmail_3]: 'Trustee Email 3',
  [EstateInputDataFormatLinkedField.TrusteePhoneNumber_3]: 'Trustee Phone Number 3',
  [EstateInputDataFormatLinkedField.TrusteeAddressCity_3]: 'Trust Address City 3',
  [EstateInputDataFormatLinkedField.TrusteeAddressState_3]: 'Trust Address State 3',
  [EstateInputDataFormatLinkedField.TrusteeAddressStreet_3]: 'Trust Address Street 3',
  [EstateInputDataFormatLinkedField.TrusteeAddressZip_3]: 'Trust Address Zip 3',

  [EstateInputDataFormatLinkedField.TrusteeFirstName_4]: 'Trustee First Name 4',
  [EstateInputDataFormatLinkedField.TrusteeLastName_4]: 'Trustee Last Name 4',
  [EstateInputDataFormatLinkedField.TrusteeMiddleName_4]: 'Trustee Middle Name 4',
  [EstateInputDataFormatLinkedField.TrusteeEmail_4]: 'Trustee Email 4',
  [EstateInputDataFormatLinkedField.TrusteePhoneNumber_4]: 'Trustee Phone Number 4',
  [EstateInputDataFormatLinkedField.TrusteeAddressCity_4]: 'Trust Address City 4',
  [EstateInputDataFormatLinkedField.TrusteeAddressState_4]: 'Trust Address State 4',
  [EstateInputDataFormatLinkedField.TrusteeAddressStreet_4]: 'Trust Address Street 4',
  [EstateInputDataFormatLinkedField.TrusteeAddressZip_4]: 'Trust Address Zip 4',

  [EstateInputDataFormatLinkedField.TrusteeFirstName_5]: 'Trustee First Name 5',
  [EstateInputDataFormatLinkedField.TrusteeLastName_5]: 'Trustee Last Name 5',
  [EstateInputDataFormatLinkedField.TrusteeMiddleName_5]: 'Trustee Middle Name 5',
  [EstateInputDataFormatLinkedField.TrusteeEmail_5]: 'Trustee Email 5',
  [EstateInputDataFormatLinkedField.TrusteePhoneNumber_5]: 'Trustee Phone Number 5',
  [EstateInputDataFormatLinkedField.TrusteeAddressCity_5]: 'Trust Address City 5',
  [EstateInputDataFormatLinkedField.TrusteeAddressState_5]: 'Trust Address State 5',
  [EstateInputDataFormatLinkedField.TrusteeAddressStreet_5]: 'Trust Address Street 5',
  [EstateInputDataFormatLinkedField.TrusteeAddressZip_5]: 'Trust Address Zip 5',
};

export const linkedEstateDocumentFieldOptions: Partial<
  Record<keyof Estate_CreateEstateInput, string>
> = {
  documentIdLettersTestamentary: 'Letters Testamentary',
  documentIdDeathCertificate: 'Death Certificate',
  documentIdFrontOfPhotoId: 'Front of Photo ID',
  documentIdBackOfPhotoId: 'Back of Photo ID',
  documentIdLegalDocumentation: 'Legal Documentation',
};

type EstateInputDataFormatFormProps = {
  isOneOffRequiredInfoFormat?: boolean;
};

const EstateInputDataFormatForm = ({
  isOneOffRequiredInfoFormat = false,
}: EstateInputDataFormatFormProps) => {
  const classes = useStyles();

  const { data: documentTagsData } = useDocumentTag_GetAllDocumentTagsQuery({
    onError: (error) => {
      showAlert({
        message: 'Error getting all document tags: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    formState: { errors },
    control,
    setValue,
    watch,
  } = useFormContext<EstateInputDataFormat_CreateEstateInputDataFormatInput>();

  const type = watch('type');
  const dataType = watch('dataType');
  const documentTags = documentTagsData?.data || [];

  return (
    <div className={classes.container}>
      <div className={classes.container}>
        <Controller
          name="name"
          control={control}
          render={({ field: { ref, onChange, ...field } }) => (
            <FormTextField
              {...field}
              inputRef={ref}
              label="Name"
              autoFocus
              onChange={(evt) => onChange(evt.target.value)}
              inputProps={{ maxLength: 80 }}
              errorMessage={errors.name?.message}
            />
          )}
        />
      </div>

      <div className={classes.container}>
        <Controller
          name="labelHint"
          control={control}
          render={({ field: { ref, onChange, ...field } }) => (
            <FormTextField
              {...field}
              inputRef={ref}
              label="Label Hint"
              onChange={(evt) => onChange(evt.target.value)}
              errorMessage={errors.labelHint?.message}
            />
          )}
        />
      </div>

      <div>
        <Controller
          name="type"
          control={control}
          render={({ field: { ref: _, onChange, ...field } }) => (
            <FormSelectField
              {...field}
              errorMessage={errors.type?.message}
              onChange={(evt) => {
                const value = evt.target.value as EstateInputDataFormatType;
                if (value !== EstateInputDataFormatType.Document) {
                  setValue('documentTagId', null);
                }
                onChange(evt.target.value);
              }}
              options={Object.values(EstateInputDataFormatType).map((type) => ({
                value: type,
                label: <div className={classes.label}>{type.replace('_', ' ')}</div>,
              }))}
              label="Type"
            />
          )}
        />
      </div>
      {type === EstateInputDataFormatType.Data && (
        <Controller
          name="dataType"
          control={control}
          render={({ field: { ref: _, onChange, ...field } }) => (
            <FormSelectField
              {...field}
              errorMessage={errors.dataType?.message}
              onChange={(evt) => {
                // TODO TD - this may need to be improved to check if list options exist
                if (dataType === EstateInputDataFormatDataType.List) {
                  showToast({
                    message:
                      'Changing the data type from type list will delete all list options. Are you sure you want to proceed?',
                    severity: NotificationSeverity.WARNING,
                  });
                }
                onChange(evt.target.value);
              }}
              options={Object.values(EstateInputDataFormatDataType).map((type) => ({
                value: type,
                label: <div className={classes.label}>{type.replace('_', ' ')}</div>,
              }))}
              label="Data Type"
            />
          )}
        />
      )}
      <div>
        <Controller
          name="category"
          control={control}
          render={({ field: { ref: _, ...field } }) => (
            <FormSelectField
              {...field}
              errorMessage={errors.category?.message}
              options={Object.values(EstateInputDataFormatCategory).map((category) => ({
                value: category,
                label: <div className={classes.label}>{category.replace('_', ' ')}</div>,
              }))}
              label="Category"
            />
          )}
        />
      </div>
      <div>
        <Controller
          name="subcategory"
          control={control}
          render={({ field: { ref, onChange, ...field } }) => (
            <FormTextField
              {...field}
              inputRef={ref}
              label="Subcategory"
              autoFocus
              onChange={(evt) => onChange(evt.target.value)}
              inputProps={{ maxLength: 80 }}
              errorMessage={errors.name?.message}
            />
          )}
        />
      </div>
      <div>
        <Controller
          name="updateRule"
          control={control}
          render={({ field: { ref: _, ...field } }) => (
            <FormSelectField
              {...field}
              errorMessage={errors.updateRule?.message}
              options={Object.values(EstateInputDataFormatUpdateRule).map((updateRule) => ({
                value: updateRule,
                label: <div className={classes.label}>{updateRule.replace('_', ' ')}</div>,
              }))}
              label="Update Rule"
            />
          )}
        />
      </div>
      {type === EstateInputDataFormatType.Document && (
        <div>
          <Controller
            name="documentTagId"
            control={control}
            render={({ field: { ref: _, ...field } }) => (
              <FormSelectField
                {...field}
                errorMessage={errors.documentTagId?.message}
                options={documentTags.map((documentTag) => ({
                  value: documentTag.id,
                  label: (
                    <div className={classes.pillContainer}>
                      <div
                        style={{
                          backgroundColor: documentTag.pillBackgroundColor,
                          color: documentTag.pillTextColor,
                        }}
                        className={classes.pill}
                      >
                        {documentTag.name}
                      </div>
                    </div>
                  ),
                }))}
                label="Document Tag"
              />
            )}
          />
        </div>
      )}
      <div className={classes.infoText}>
        Also update the following estate field. Leave blank if this doesn't update the estate
      </div>

      {type === EstateInputDataFormatType.Data && (
        <div>
          <Controller
            name="linkedField"
            control={control}
            render={({ field: { ref: _, ...field } }) => (
              <FormSelectField
                {...field}
                errorMessage={errors.linkedField?.message}
                includeNullOption
                options={Object.entries(linkedEstateMatterFieldOptions).map(([value, label]) => ({
                  value,
                  label,
                }))}
                label="Field in Estate"
              />
            )}
          />
        </div>
      )}

      {!isOneOffRequiredInfoFormat && (
        <div className={classes.container}>
          <Controller
            name="templateFieldKey"
            control={control}
            render={({ field: { ref, onChange, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                label="Template Field Key"
                onChange={(evt) => onChange(evt.target.value)}
                inputProps={{ maxLength: 50 }}
                errorMessage={errors.templateFieldKey?.message}
              />
            )}
          />
        </div>
      )}

      <div className={classes.container}>
        <Controller
          name="omitEmptyPlaceholder"
          control={control}
          render={({ field: { ref, onChange, value, ...field } }) => (
            <FormSelectField
              {...field}
              inputRef={ref}
              label="Omit Empty Placeholder"
              onChange={(e) => onChange(e.target.value === 'yes')}
              value={value ? 'yes' : 'no'}
              options={[
                {
                  label: 'Yes',
                  value: 'yes',
                },
                {
                  label: 'No',
                  value: 'no',
                },
              ]}
              errorMessage={errors.omitEmptyPlaceholder?.message}
            />
          )}
        />
      </div>
    </div>
  );
};

export default EstateInputDataFormatForm;
